import { useTranslations } from 'next-intl';
import { useUser } from '@mentimeter/user';
import { Form, Select, TextInput } from '@mentimeter/ragnar-form';
import * as Yup from 'yup';
import { Box } from '@mentimeter/ragnar-ui/box';
import { useButtonState } from '@mentimeter/ragnar-ui/button';
import { useMemo } from 'react';
import { useGetErrorMessage } from './handleErrorMessage';
import type { JoinErrorEnum } from './handleErrorMessage';
import { handleSubmit } from './utils';
import { VerifyEmailButton } from './VerifyEmailButton';

const splitEmailAddress = ({ userEmail }: { userEmail: string }) => {
  const splitEmail = userEmail.split('@');
  const userName = splitEmail[0];
  const userDomain = splitEmail[1];

  return { userName, userDomain };
};

const useValidationSchema = () => {
  const t = useTranslations('onboarding.join_workspace');

  return useMemo(
    () =>
      Yup.object().shape({
        email: Yup.string()
          .required(t('email_without_domain_required_message'))
          .max(100, t('email_without_domain_too_long_message'))
          .matches(/^((?!@).)*$/, t('email_without_domain_invalid_message')),
        domain: Yup.string().required(t('domain_required_message')),
      }),
    [t],
  );
};
export const RestrictedDomainsForm = ({
  emailDomains,
  teamUrl,
  teamName,
  onClaimInviteSuccess,
  onClaimInviteError,
}: {
  emailDomains: Array<string>;
  teamUrl: string;
  teamName: string;
  onClaimInviteSuccess: ({
    status,
    email,
  }: { status: number; email: string }) => void;
  onClaimInviteError: (error: JoinErrorEnum) => void;
}) => {
  const t = useTranslations('onboarding.join_workspace');
  const { user } = useUser();
  const [buttonState, setButtonState] = useButtonState();
  const schema = useValidationSchema();
  const getErrorMessage = useGetErrorMessage();

  const { userName, userDomain } = splitEmailAddress({
    userEmail: user ? user.email : '',
  });

  return (
    <Form
      initialValues={{
        email: userName,
        domain: userDomain || emailDomains[0],
      }}
      validationSchema={schema}
      onSubmit={async (data, formikActions) => {
        setButtonState('loading');
        try {
          const email = `${data.email.trim()}@${data.domain}`;
          const { status } = await handleSubmit({
            email,
            url: teamUrl,
          });

          onClaimInviteSuccess({ status, email });

          setButtonState('success');
        } catch (e: any) {
          const error = e.response?.data?.code || 'default';
          const errorMessage = getErrorMessage({
            error,
            workspaceName: teamName,
          });

          onClaimInviteError(error);
          formikActions.setSubmitting(false);
          formikActions.setErrors({ email: errorMessage.formError });
          setButtonState('error');
        }
      }}
      width={1}
      alignItems="center"
    >
      <Box
        flexDirection="row"
        justifyContent="center"
        alignItems="normal"
        width="100%"
      >
        <TextInput
          aria-label={t('email_input_without_domain_aria_label_a11y')}
          m={0}
          flex="1 1 auto"
          name="email"
          label={t('email_address_label')}
          placeholder={t('email_placeholder')}
        />
        <Select
          aria-label={t('company_domain_aria_label_a11y')}
          ml={1}
          width={[null, '50%']}
          label="&nbsp;"
          name="domain"
          key="domain"
          disabled={emailDomains.length === 1}
          options={emailDomains.map((domain) => ({
            key: domain,
            value: domain,
            label: `@${domain}`,
          }))}
        />
      </Box>
      <VerifyEmailButton buttonState={buttonState} />
    </Form>
  );
};
